import React, { useMemo } from 'react';
import { StickerDesignId, Board } from '../utils/Primitives';
import { formatDistanceToNow } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { getStickerDesign, getStickerUrl, getOrLoadStickerDesign, getUrlForCID } from '../stickers/StickerDesigns';
import { fetchPlayer } from '../player/Players';
import AddressDisplay from '../components/AddressDisplay';

interface PublishActivityProps {
  stickerId: StickerDesignId;
  stickerName: string;
  imageCID: string;
  timestamp: number;
  boardRef: React.MutableRefObject<Board>;
  onStickerClick: (stickerId: StickerDesignId) => void;
  isIndexing?: boolean;
}

const PublishActivity: React.FC<PublishActivityProps> = ({
  stickerId,
  stickerName,
  imageCID,
  timestamp,
  boardRef,
  onStickerClick,
  isIndexing = false,
}) => {
  // Memoize the time ago string to prevent unnecessary recalculations
  const timeAgo = useMemo(() =>
    formatDistanceToNow(timestamp, { addSuffix: true }),
    [timestamp]
  );

  // Memoize the existing sticker check
  const existingSticker = useMemo(() =>
    getStickerDesign({ boardRef, stickerId }),
    [boardRef, stickerId]
  );

  const player = useMemo(() =>
    existingSticker ? fetchPlayer({ boardRef, address: existingSticker.currentPublisher }) : null,
    [boardRef, existingSticker]
  );

  // Show indexing state if either:
  // 1. We're explicitly told it's indexing, OR
  // 2. The sticker isn't in game state yet
  const showIndexing = isIndexing || !existingSticker;

  // Only query if we're showing indexing state
  const { isFetching } = useQuery({
    queryKey: ['sticker', stickerId],
    queryFn: () => getOrLoadStickerDesign({ boardRef, stickerId }),
    enabled: showIndexing,
    staleTime: 30000,
    gcTime: 60000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const handleClick = () => {
    if (existingSticker) {
      onStickerClick(stickerId);
    }
  };

  // Memoize the image URLs
  const activityImageUrl = useMemo(() =>
    getUrlForCID(imageCID),
    [imageCID]
  );

  const stickerImageUrl = useMemo(() =>
    existingSticker ? getStickerUrl(existingSticker) : null,
    [existingSticker]
  );

  return (
    <div
      className={`flex items-start space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors ${existingSticker ? 'cursor-pointer' : ''}`}
      onClick={handleClick}
    >
      <div className="flex-shrink-0 w-14 h-14 rounded-lg bg-blue-100 flex items-center justify-center overflow-hidden">
        {/* Show sticker image if it exists in game state, otherwise show activity image */}
        {stickerImageUrl ? (
          <img
            src={stickerImageUrl}
            alt={stickerName}
            className="w-full h-full object-cover"
          />
        ) : (
          <img
            src={activityImageUrl}
            alt={stickerName}
            className="w-full h-full object-cover"
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 break-words max-w-[200px]">
          {showIndexing ? (
            <span className="flex items-center">
              <span className="animate-pulse mr-2">⏳</span>
              Sticker indexing...
            </span>
          ) : (
            `Published Sticker`
          )}
        </p>
        {existingSticker && (
          <p className="text-xs text-gray-600">
            <AddressDisplay
              address={existingSticker.currentPublisher}
              ensName={existingSticker.currentPublisherEns || player?.ens}
            />
          </p>
        )}
        <p className="text-xs text-gray-500">{timeAgo}</p>
      </div>
    </div>
  );
};

export default PublishActivity;
